import { useRef } from 'react';
import ReactGA from 'react-ga4';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { Navigation } from 'swiper';
import { Swiper, SwiperRef, SwiperSlide } from 'swiper/react';
import { ampli } from '../../../../ampli';
import BGImage from '../../../../assets/images/bg_home_youtube_section.jpg';
import { YoutubeSection } from '../../../../components/common/video/YoutubeSection';
import { pageInfo } from '../../../../constants/pageInfo/pageInfo.constant';

interface IYoutubeReview {
  title: string;
  subtitle: string;
  youtubeUrl: string;
  productDetailUrl: string;
  code: string;
  buttonText: string;
}

const youtubeReviews: IYoutubeReview[] = [
  {
    title: '이앙기와 트랙터 자율주행!',
    subtitle: '모판 넣을 시간에 1줄 더 심는\n플루바 오토, 사용자 실제 후기 영상',
    youtubeUrl: 'https://www.youtube.com/watch?v=reIkp5jcbVY',
    productDetailUrl: pageInfo.pluvaAutoPage.path,
    code: 'pluva-auto',
    buttonText: '플루바 오토 보러가기',
  },
  {
    title: '퓨마 150 실물 영상 리뷰!',
    subtitle: '작업 이력은 제로! 신품 대비\n7천만원 저렴한 최상급 중고 트랙터!',
    youtubeUrl: 'https://www.youtube.com/watch?v=NtqpYtJsO6Q',
    productDetailUrl: '/products/4f65ef58-882c-48ad-99e0-31bd9ba5cc0f?category=PLUVA',
    code: '045-CA150KO106',
    buttonText: '매물 보러가기',
  },
  {
    title: '존디어 6215R 실물 영상 리뷰!',
    subtitle: '완벽하게 정비된 깔끔한 중고,\n존디어를 영상으로 만나보세요',
    youtubeUrl: 'https://www.youtube.com/watch?v=8OmysaPGZjM',
    productDetailUrl: '/products/f11a8068-aa57-4e09-9129-82703317988b?category=PLUVA',
    code: '005-JD5EU11',
    buttonText: '매물 보러가기',
  },
];

export const YoutubeReviewSection = () => {
  const swiperRef = useRef<SwiperRef>(null);

  const stopAutoPlay = () => {
    swiperRef?.current?.swiper?.autoplay?.stop();
  };

  const startAutoPlay = () => {
    swiperRef?.current?.swiper?.autoplay?.start();
  };

  const navigate = useNavigate();
  return (
    <YoutubeSectionWrapper>
      <Container path={BGImage}>
        <SwiperWrapper id={'home-youtube-section'}>
          <SwiperButtonPrev className="swiper-button-prev" />
          <SwiperButtonNext className="swiper-button-next" />
          <CustomSwiper
            ref={swiperRef}
            modules={[Navigation]}
            slidesPerView={1}
            spaceBetween={16}
            navigation={{
              prevEl: '.swiper-button-prev',
              nextEl: '.swiper-button-next',
            }}
            autoplay={{
              delay: 5000,
            }}
            pagination
          >
            {youtubeReviews.map((youtubeReview: IYoutubeReview) => (
              <CustomSwiperSlide key={youtubeReview.youtubeUrl}>
                <TitleSection>
                  <Title>{youtubeReview.title}</Title>
                  <SubTitle>{youtubeReview.subtitle}</SubTitle>
                </TitleSection>
                <YoutubeWrapper>
                  <YoutubeSection
                    youtubeUrl={youtubeReview.youtubeUrl}
                    onPlay={() => {
                      stopAutoPlay();
                      ampli.pvmClickHomeVideoPlayButton({
                        '매물 코드': youtubeReview.code,
                      });
                    }}
                    onEnd={() => startAutoPlay()}
                  />
                </YoutubeWrapper>
                <ProductSummary>
                  <Button
                    onClick={() => {
                      ampli.pvmClickHomeVideoPdpViewButton({
                        '매물 코드': youtubeReview.code,
                      });
                      ReactGA.event('pvm_click_home_video_pdp_view_button', {
                        category: 'home',
                        action: 'pvm_click_home_video_pdp_view_button',
                        product_code: youtubeReview.code,
                      });
                      navigate(youtubeReview.productDetailUrl);
                    }}
                  >
                    <ButtonText>{youtubeReview.buttonText}</ButtonText>
                  </Button>
                </ProductSummary>
              </CustomSwiperSlide>
            ))}
          </CustomSwiper>
        </SwiperWrapper>
      </Container>
    </YoutubeSectionWrapper>
  );
};

const YoutubeSectionWrapper = styled.div`
  display: flex;
  padding: 0px var(--radius-large, 16px);
  padding-bottom: 32px;
  flex-direction: column;
  align-items: flex-start;
  gap: 10px;
  align-self: stretch;
`;

const Container = styled.div<{ path: string }>`
  position: relative;
  display: flex;
  padding: 40px 48px;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 24px;
  align-self: stretch;
  border-radius: var(--radius-medium, 8px);
  background: ${(props) => `url(${props.path})`} lightgray 50% / cover no-repeat;
`;

const TitleSection = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: var(--radius-medium, 8px);
  align-self: stretch;
`;

const Title = styled.h3`
  color: var(--Light-Scheme-Monochrome-white, #fff);
  text-align: center;

  /* Display/XX-Large/xxl-bold */
  font-family: Pretendard;
  font-size: 20px;
  font-style: normal;
  font-weight: 700;
  line-height: 130%; /* 26px */
`;

const SubTitle = styled.p`
  color: var(--Light-Scheme-Monochrome-grey20, #e4e4ed);
  text-align: center;

  /* Display/Large/l-light */
  font-family: Pretendard;
  font-size: 16px;
  font-style: normal;
  font-weight: 300;
  line-height: 130%; /* 20.8px */

  white-space: pre-line;
`;

const SwiperWrapper = styled.div`
  width: 100%;
  color: var(--Light-Scheme-Monochrome-white, #fff);
`;

const CustomSwiper = styled(Swiper)`
  width: 100%;

  gap: 16px;
`;

const CustomSwiperSlide = styled(SwiperSlide)`
  display: flex;
  min-width: 152px;
  margin-bottom: 42px;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 24px;

  cursor: pointer;
`;

const CommonSwiperButtonStyle = styled.button`
  display: flex;
  width: 30px;
  height: 30px;
  padding: var(--radius-medium, 8px);
  justify-content: center;
  align-items: center;
  gap: 10px;

  border-radius: var(--radius-round, 80px);
  background: rgba(255, 255, 255, 0.4);
  backdrop-filter: blur(2px);
  ::after {
    color: #505363;
    text-shadow: none;
  }
`;

const SwiperButtonPrev = styled(CommonSwiperButtonStyle)``;
const SwiperButtonNext = styled(CommonSwiperButtonStyle)``;

const YoutubeWrapper = styled.div`
  width: 100%;
  display: flex;
  justify-content: flex-start;
  align-items: flex-start;
`;

const ProductSummary = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 24px;
  align-self: stretch;
`;

const Button = styled.button`
  display: flex;
  width: 150px;
  max-height: 40px;
  padding: 16px;
  justify-content: center;
  align-items: center;
  gap: 4px;

  border-radius: var(--radius-medium, 8px);
  background: var(--Light-Scheme-Color-primary-blue, #445eff);
  white-space: nowrap;
`;

const ButtonText = styled.span`
  color: var(--Light-Scheme-Monochrome-white, #fff);

  /* System/Medium/m-medium */
  font-family: Pretendard;
  font-size: 14px;
  font-style: normal;
  font-weight: 500;
  line-height: 85%; /* 11.9px */
`;
